/**
 * DATA CONTRACT — the shape of every runtime data asset.
 * Owned by the main loop. Agents implement against it; they never edit it.
 *
 * Runtime files (logical names; hashed copies are served from /a/ via /manifest.json):
 *   assets/data/places.json    → Dataset
 *   assets/data/routes.json    → RoutesFile
 *   assets/data/site.geojson   → SiteCollection   (site polygon + origin point)
 *   assets/data/roads.geojson  → CorridorCollection (existing arterial roads)
 *   assets/data/future.geojson → CorridorCollection (proposed / under-construction)
 *   assets/map/contours.geojson→ ContourCollection
 *
 * Coordinates are always [lng, lat] (GeoJSON order), WGS84, rounded to 6 dp.
 * This file must stay "erasable TypeScript" (no enums/namespaces) so Node can
 * import it directly from scripts (node --experimental-strip-types default).
 */
import { z } from "zod";

/* ─── Primitives ─────────────────────────────────────────────────────────── */

export const LngLatSchema = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
export type LngLat = z.infer<typeof LngLatSchema>;

export const BBoxSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]); // w, s, e, n
export type BBox = z.infer<typeof BBoxSchema>;

export const GroupIdSchema = z.enum(["A", "B", "C", "D"]);
export type GroupId = z.infer<typeof GroupIdSchema>;

/** Where a place's coordinate came from. */
export const PlaceSourceSchema = z.enum(["osm", "client-pin", "geocode", "manual"]);
export const ConfidenceSchema = z.enum(["high", "medium", "low"]);
export const PlaceStatusSchema = z.enum(["existing", "upcoming", "proposed", "under-construction"]);
export type PlaceStatus = z.infer<typeof PlaceStatusSchema>;

/** Category kinds decide how a place is drawn. poi = point; road/future = a
 *  corridor geometry (see geometryRef); area = a neighbourhood (urban centre);
 *  hub = airport / railway station (long routes). */
export const CategoryKindSchema = z.enum(["poi", "road", "future", "area", "hub"]);
export type CategoryKind = z.infer<typeof CategoryKindSchema>;

/* ─── Taxonomy ───────────────────────────────────────────────────────────── */

export const GroupSchema = z.object({
  id: GroupIdSchema,
  label: z.string(), //   "Physical infrastructure"
  short: z.string(), //   "Infrastructure" — used in the segmented control
  icon: z.string(), //    IconName from src/design/icons.tsx
  order: z.number().int(),
});
export type Group = z.infer<typeof GroupSchema>;

export const CategorySchema = z.object({
  id: z.string(), //      kebab id, see CATEGORY_IDS in taxonomy.ts
  group: GroupIdSchema,
  label: z.string(), //   "Education"
  sourceLabel: z.string(), // exact Excel sub-category text, e.g. "Lifestyle / retail"
  order: z.number().int(),
  icon: z.string(),
  kind: CategoryKindSchema,
});
export type Category = z.infer<typeof CategorySchema>;

/* ─── Places ─────────────────────────────────────────────────────────────── */

export const ExtraPinSchema = z.object({
  coord: LngLatSchema,
  note: z.string(), //    e.g. "Connection point once the Inner Ring Road is built"
  future: z.boolean(),
  gmapsUrl: z.string().optional(),
});
export type ExtraPin = z.infer<typeof ExtraPinSchema>;

export const RouteSummarySchema = z.object({
  routeId: z.string(), //  RouteRecord.id (== place id)
  km: z.number().positive(), //       road km, 1 dp (OSRM)
  minutes: z.number().positive(), //  off-peak (free-flow) minutes, integer
  via: z.array(z.string()).max(3), // main roads by distance, e.g. ["Lohegaon–Wagholi Road", "Nagar Road"]
  kmOverride: z.number().positive().optional(), //      marketing numbers from overrides.json win in the UI
  minutesOverride: z.number().positive().optional(),
  note: z.string().optional(), // e.g. "Under an hour once the Outer Ring Road opens"
});
export type RouteSummary = z.infer<typeof RouteSummarySchema>;

export const PlaceSchema = z.object({
  id: z.string(), //        kebab slug of the cleaned name, unique
  name: z.string(), //      cleaned display name ("St. Joseph's High School")
  originalName: z.string(), // exact Excel text (may be "" for continuation rows)
  category: z.string(), //  Category.id
  group: GroupIdSchema,
  order: z.number().int(), // Excel row order within the category
  coord: LngLatSchema, //   the pin
  locality: z.string().optional(), // "Viman Nagar", from reverse geocode or Excel note
  status: PlaceStatusSchema,
  icon: z.string().optional(), // per-place override (e.g. "train" for Pune Railway Station)
  extraPins: z.array(ExtraPinSchema),
  gmapsUrl: z.string().optional(), // the client's original short link
  notes: z.string().optional(), //    cleaned Excel note, calm brand voice
  source: PlaceSourceSchema,
  confidence: ConfidenceSchema,
  osm: z.object({ type: z.enum(["node", "way", "relation"]), id: z.number() }).optional(),
  route: RouteSummarySchema.optional(), // absent only for isSite / unreachable
  crowKm: z.number().nonnegative(), //    straight-line km from origin, 1 dp
  bearing: z.number().min(0).lt(360), //  degrees from origin, 0 = north
  geometryRef: z.string().optional(), //   feature id in roads/future geojson (CORRIDOR_IDS)
  isSite: z.boolean().optional(), //       the "Ladera City" rows themselves
});
export type Place = z.infer<typeof PlaceSchema>;

export const DatasetSchema = z.object({
  version: z.string(),
  generatedAt: z.string(),
  origin: z.object({
    coord: LngLatSchema, //      route origin: a point on the site's access road
    label: z.string(), //        "Ládera City"
    sitePin: LngLatSchema, //    the client pin (Diamond Water Parks, SW corner)
  }),
  groups: z.array(GroupSchema).length(4),
  categories: z.array(CategorySchema),
  places: z.array(PlaceSchema),
  attribution: z.array(z.string()),
});
export type Dataset = z.infer<typeof DatasetSchema>;

/* ─── Routes (for the LightLayer + card) ─────────────────────────────────── */

export const NameSpanSchema = z.object({
  fromM: z.number().nonnegative(),
  toM: z.number().nonnegative(),
  name: z.string(), //  "Nagar Road" (cleaned; falls back to ref, e.g. "NH753F")
  ref: z.string().optional(),
});
export type NameSpan = z.infer<typeof NameSpanSchema>;

export const RouteRecordSchema = z.object({
  id: z.string(), //          == place id
  placeId: z.string(),
  coords: z.array(LngLatSchema).min(2), // simplified (~5 m); coords[0] = origin, last = place pin (snapped)
  cum: z.array(z.number().nonnegative()), // cumulative metres, same length as coords, cum[0] = 0
  lod1: z.array(z.number().int().nonnegative()), // indices into coords for the coarse LOD (~40 m tolerance), includes first+last
  lengthM: z.number().positive(),
  durationS: z.number().positive(),
  bbox: BBoxSchema,
  framingBearing: z.number(), // degrees; bearing origin→destination used by camera framing
  nameSpans: z.array(NameSpanSchema),
  via: z.array(z.string()).max(3),
  snapM: z.object({ origin: z.number().nonnegative(), dest: z.number().nonnegative() }),
});
export type RouteRecord = z.infer<typeof RouteRecordSchema>;

export const RoutesFileSchema = z.object({
  version: z.string(),
  origin: LngLatSchema,
  engine: z.enum(["osrm-local", "osm-graph", "ors"]), // osm-graph = in-house Dijkstra over an OSM extract (fallback when Docker is unavailable)
  profile: z.literal("car"),
  osmDataDate: z.string(),
  routes: z.array(RouteRecordSchema),
});
export type RoutesFile = z.infer<typeof RoutesFileSchema>;

/* ─── GeoJSON collections ────────────────────────────────────────────────── */

const LineGeom = z.union([
  z.object({ type: z.literal("LineString"), coordinates: z.array(LngLatSchema).min(2) }),
  z.object({ type: z.literal("MultiLineString"), coordinates: z.array(z.array(LngLatSchema).min(2)).min(1) }),
]);

/** Corridors: arterial roads (roads.geojson) and future infrastructure (future.geojson). */
export const CorridorPropsSchema = z.object({
  id: z.string(), //  CORRIDOR_IDS value
  name: z.string(),
  ref: z.string().optional(), //  "NH753F"
  kind: z.enum(["arterial", "expressway", "ring", "dp-road", "connector"]),
  status: z.enum(["existing", "proposed", "under-construction", "indicative"]),
  source: z.string(), //  "OpenStreetMap", "Excel connection pins + PMRDA DP (schematic)", …
  sourceDate: z.string(), // ISO date of the data used
  placeId: z.string().optional(),
});
export const CorridorFeatureSchema = z.object({
  type: z.literal("Feature"),
  id: z.string().optional(),
  properties: CorridorPropsSchema,
  geometry: LineGeom,
});
export const CorridorCollectionSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(CorridorFeatureSchema),
});
export type CorridorCollection = z.infer<typeof CorridorCollectionSchema>;

/** Site: exactly one Polygon feature (id "site") + one Point (id "origin").
 *  Optional extra features: "site-pin" Point (the client pin), "gate" Points. */
export const SiteFeatureSchema = z.object({
  type: z.literal("Feature"),
  id: z.string().optional(),
  properties: z.object({
    id: z.string(), // "site" | "origin" | "site-pin" | "gate-*"
    name: z.string(),
    approximate: z.boolean(),
    source: z.string(),
    note: z.string().optional(),
  }),
  geometry: z.union([
    z.object({ type: z.literal("Polygon"), coordinates: z.array(z.array(LngLatSchema).min(4)).min(1) }),
    z.object({ type: z.literal("Point"), coordinates: LngLatSchema }),
  ]),
});
export const SiteCollectionSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(SiteFeatureSchema).min(2),
});
export type SiteCollection = z.infer<typeof SiteCollectionSchema>;

/** Contours: smoothed isolines from the DEM. ele in metres. */
export const ContourFeatureSchema = z.object({
  type: z.literal("Feature"),
  properties: z.object({
    ele: z.number(),
    major: z.boolean(), //  100 m (region) / 50 m (metro) lines
    ridge: z.boolean().optional(), // the one "ridge line" feature near the site
    lod: z.enum(["region", "metro"]),
  }),
  geometry: LineGeom,
});
export const ContourCollectionSchema = z.object({
  type: z.literal("FeatureCollection"),
  features: z.array(ContourFeatureSchema),
});
export type ContourCollection = z.infer<typeof ContourCollectionSchema>;

/** Helper: the km / minutes the UI should show (overrides win). */
export function displayTravel(p: Pick<Place, "route">): { km: number; minutes: number } | null {
  if (!p.route) return null;
  return { km: p.route.kmOverride ?? p.route.km, minutes: p.route.minutesOverride ?? p.route.minutes };
}
